import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import { useCart } from '@/hooks/use-cart'
import { useCredit } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { CartIcon } from '@/ui/icons/cart'

interface CartButtonProps {
  onOpenCart: (open: boolean) => void
}

export const CartButton = ({ onOpenCart }: CartButtonProps) => {
  const { t, i18n } = useTranslation()

  const posthog = usePostHog()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const currency = useAppSelector(
    (store) => store.returnsApi.order.response.order?.money_format?.currency
  )

  const { amountToPay } = useCredit()

  const { cartItems, getCartTotal } = useCart()

  const cartTotalQuantity = getCartTotal(cartItems)

  const handleCartClick = () => {
    posthog.capture('FC - Click show cart', { checkout_id: checkoutId })

    onOpenCart(true)
  }

  const isMisako = ecommerceGroup === 'misako'
  const isHoff = ecommerceGroup === 'hoff'

  if (isHoff) {
    return (
      <button
        id="cart-button"
        className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-white px-4 py-1.5 text-base font-medium tabular-nums"
        onClick={handleCartClick}
      >
        <CartIcon width={18} height={18} />
        {t('full_catalog.cart_items', { count: cartTotalQuantity })}
      </button>
    )
  }

  if (isMisako) {
    return (
      <button
        id="cart-button"
        className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-white px-4 py-1.5 text-base font-medium tabular-nums"
        onClick={handleCartClick}
      >
        <CartIcon width={18} height={18} />
        {t('full_catalog.cart_items', { count: cartTotalQuantity })}
      </button>
    )
  }

  return (
    <div className="relative flex shrink-0 items-center overflow-hidden rounded-full bg-radial-gradient from-brand-200 to-brand-100 px-1 py-[3.5px] shadow ring-1 ring-brand-200 transition-shadow hover:shadow-sm">
      {cartTotalQuantity > 0 ? (
        <div className="animate-expand">
          <p className="flex items-center gap-1.5 truncate pl-3.5 pr-3 text-xs font-normal text-brand-700">
            {t('full_catalog.pay_only')}{' '}
            <strong className="text-sm font-semibold md:text-base">
              {amountToPay.toLocaleString(i18n.language, {
                style: 'currency',
                currency,
                minimumFractionDigits: amountToPay === 0 ? 0 : 2,
                maximumFractionDigits: 2
              })}
            </strong>
          </p>
        </div>
      ) : null}
      <button
        id="cart-button"
        className="z-10 flex shrink-0 items-center justify-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-medium tabular-nums shadow-sm transition-background before:absolute before:inset-0 before:z-10 hover:bg-neutral-50"
        onClick={handleCartClick}
      >
        <CartIcon width={18} height={18} />
        {t('full_catalog.cart_items', { count: cartTotalQuantity })}
      </button>
    </div>
  )
}
